import React, { useEffect, useState } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import SectionSidebar, {
  SectionNavItem,
  SECTION_NAV_OFFSET
} from './SectionSidebar';

interface SectionNavLayoutProps {
  title: string;
  sections: SectionNavItem[];
  children: React.ReactNode;
}

const SectionNavLayout: React.FC<SectionNavLayoutProps> = ({
  title,
  sections,
  children
}) => {
  const [activeSection, setActiveSection] = useState<string>(
    sections[0]?.id ?? ''
  );

  const navigateToSection = (id: string) => {
    const container = document.getElementById('scrollable-wrapper');
    const target = document.getElementById(id);

    if (container && target) {
      const y =
        target.getBoundingClientRect().top +
        container.scrollTop -
        SECTION_NAV_OFFSET;

      container.scrollTo({ top: y, behavior: 'smooth' });
    }

    setActiveSection(id);
  };

  useEffect(() => {
    const container = document.getElementById('scrollable-wrapper');
    if (!container) return;

    const sectionElements = sections
      .map(section => document.getElementById(section.id))
      .filter((el): el is HTMLElement => el !== null);

    if (sectionElements.length === 0) return;

    const handleScroll = () => {
      let currentId = sections[0]?.id ?? '';

      for (const el of sectionElements) {
        if (el.getBoundingClientRect().top <= SECTION_NAV_OFFSET) {
          currentId = el.id;
        } else {
          break;
        }
      }

      const isAtBottom =
        container.scrollTop + container.clientHeight >=
        container.scrollHeight - 2;

      if (isAtBottom) {
        currentId = sections[sections.length - 1]?.id ?? currentId;
      }

      setActiveSection(currentId);
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => container.removeEventListener('scroll', handleScroll);
  }, [sections]);

  return (
    <Flex width="100%" gap={6} alignItems="flex-start">
      {/* Desktop: sticky sidebar */}
      <Box
        display={{ base: 'none', md: 'block' }}
        width="240px"
        flexShrink={0}
        position="sticky"
        top={`${SECTION_NAV_OFFSET}px`}
        height={`calc(100vh - ${SECTION_NAV_OFFSET}px)`}
        overflowY="auto"
        bgColor="surfacePrimaryFill"
        borderRadius="10px"
        padding={2}
      >
        <SectionSidebar
          title={title}
          sections={sections}
          activeId={activeSection}
          onNavigate={navigateToSection}
        />
      </Box>

      <Flex flexDirection="column" gap={6} flex="1" minWidth={0}>
        {/* Mobile: horizontal nav above content */}
        <Box display={{ base: 'block', md: 'none' }}>
          <SectionSidebar
            title={title}
            sections={sections}
            activeId={activeSection}
            onNavigate={navigateToSection}
          />
        </Box>

        {children}
      </Flex>
    </Flex>
  );
};

export default SectionNavLayout;
